import React, { useContext, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import { getProductsByStore } from "../../../api/productsApi";
import { LanguageContext } from "../../../components/LanguageContext";

const Empty = () => (
    <Typography variant="body2" color="text.disabled">
        —
    </Typography>
);

const ProductsByStorePage = () => {
    const { storeCode } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const { data: productsByStore, isLoading } = useQuery({
        queryKey: ["productsByStore", storeCode],
        queryFn: () => getProductsByStore(storeCode),
        staleTime: 5 * 60 * 1000,
    });

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "supplier_name",
                headerName: translations?.adminProductPageSupplier || "Dobavljač",
                minWidth: 180,
                flex: 1,
                renderCell: (params) =>
                    params.value ? (
                        <Typography
                            variant="body2"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={params.value}
                        >
                            {params.value}
                        </Typography>
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "code",
                headerName:
                    translations?.adminInventoryPageProductCode || "Šifra proizvoda",
                minWidth: 150,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminInventoryPageProductName || "Naziv proizvoda",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={(theme) => ({
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(15,23,42,0.04)",
                                color: "primary.main",
                            })}
                        >
                            <Inventory2OutlinedIcon fontSize="small" />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={params.value}
                        >
                            {params.value}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: "ean",
                headerName: "EAN",
                minWidth: 150,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.value ? (
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", letterSpacing: "0.05em" }}
                        >
                            {params.value}
                        </Typography>
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "group",
                headerName: translations?.adminProductPageGroup || "Grupa",
                minWidth: 150,
                flex: 1,
                renderCell: (params) =>
                    params.value ? (
                        <Chip
                            label={params.value}
                            size="small"
                            sx={(theme) => ({
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(15,23,42,0.04)",
                            })}
                        />
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "subgroup",
                headerName: translations?.subgroup || "Podgrupa",
                minWidth: 150,
                flex: 1,
                renderCell: (params) =>
                    params.value ? (
                        <Chip label={params.value} size="small" variant="outlined" />
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "lead_time",
                headerName: "Lead time",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LocalShippingOutlinedIcon
                            sx={{ fontSize: "1rem", color: "text.secondary" }}
                        />
                        <Typography sx={{ fontWeight: 600 }}>
                            {params.row.lead_time}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: "last_supply_price",
                headerName: translations?.lastSupplyPrice || "Posl. nabavna",
                minWidth: 160,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <EuroOutlinedIcon
                            sx={{ fontSize: "1rem", color: "text.secondary" }}
                        />
                        <Typography
                            sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                            {params.row.last_supply_price}
                        </Typography>
                    </Stack>
                ),
            },
        ],
        [translations]
    );

    const rows = useMemo(
        () => productsByStore?.data || [],
        [productsByStore]
    );

    return (
        <Box>
            <PageHeader
                icon={<Inventory2OutlinedIcon />}
                eyebrow={translations?.productsByStore || "Products"}
                title={
                    translations?.productsByStore || "Proizvodi po prodavnici"
                }
                description={`${translations?.productsSubtitle || "Pregled proizvoda u prodavnici"} ${storeCode}`}
            />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    rowHeight={64}
                    emptyIcon={<Inventory2OutlinedIcon />}
                    emptyTitle={translations?.noProductsTitle || "Nema proizvoda"}
                    emptyDescription={
                        translations?.noProductsDescription ||
                        "Sinhronizujte proizvode iz Navigatora."
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default ProductsByStorePage;